import React from "react";
import { createRoot } from "react-dom/client";
import { createPortal } from "react-dom";

const { useState, useEffect, useCallback, memo, Suspense } = React;


const componentRegistry = {
    email: React.lazy(() => import("./Email")),
    video: React.lazy(() => import("./Video")),
    documentary: React.lazy(() => import("./Documentary")),
    builder: React.lazy(() => import("./Builder")),
    warning: React.lazy(() => import("./Warning")),
    ranking: React.lazy(() => import("./Ranking"))
};

function getComponent(name) {
    const Component = componentRegistry[name];
    if (!Component) {
        console.warn(`Component "${name}" not found in registry.`);
        return null;
    }
    return Component;
}

function LoaderHider() {
    useEffect(() => {
        if (typeof window.hideLoader === 'function') window.hideLoader();
    }, []);
    return null;
}

function ComponentRenderer({ name, props }) {
    const Component = getComponent(name);
    if (!Component) return null;

    return (
        <Suspense fallback={<div>...</div>}>
            <Component {...props} />
            <LoaderHider />
        </Suspense>
    );
}

class DialogController {
    pushHandler = null;
    popHandler = null;

    bind(push, pop) {
        this.pushHandler = push;
        this.popHandler = pop;
    }

    push(dialog) {
        this.pushHandler?.(dialog);
    }

    pop() {
        this.popHandler?.();
    }

    cleanup() {
        this.pushHandler = null;
        this.popHandler = null;
    }
}

const dialogController = new DialogController();

const Dialog = memo(({ dialog, onClose, zIndex }) => {
    useEffect(() => {
        const onEsc = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [onClose]);

    const stop = useCallback((e) => e.stopPropagation(), []);
    const backdropClick = useCallback(
        (e) => e.target === e.currentTarget && onClose(),
        [onClose]
    );

    // "fit": the card fits its content (e.g. the video's size) and the
    // background is a translucent veil instead of the theme's opaque backdrop.
    const fitted = dialog.size === "fit";

    return createPortal(
        <div
            onClick={backdropClick}
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: "100vw",
                maxWidth: "100vw",
                margin: 0,
                padding: "0 12px",
                boxSizing: "border-box",
                backgroundColor: fitted ? "rgba(2, 6, 23, .45)" : "var(--theme)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex,
            }}
        >
            <div
                className={`card animated slide ${fitted ? "" : (dialog.size || "small")}`}
                style={{ maxWidth: "calc(100vw - 24px)", ...(fitted ? { width: "fit-content" } : null) }}
                onClick={stop}
            >
                <button
                    onClick={onClose}
                    style={fitted
                        // Overlaid on the content: in fitted dialogs there is
                        // no title and floating it would leave an empty row.
                        ? { position: "absolute", top: 8, right: 8, zIndex: 1 }
                        : { float: "right" }}
                >
                    ×
                </button>
                {dialog.title && <h3>{dialog.title}</h3>}
                <ComponentRenderer name={dialog.component} props={dialog.props} />
            </div>
        </div>,
        document.body
    );
});

Dialog.displayName = "Dialog";

function Application() {
    const [dialogs, setDialogs] = useState([]);

    const push = useCallback((dialog) => {
        setDialogs((prev) => [...prev, dialog]);
    }, []);

    const pop = useCallback(() => {
        setDialogs((prev) => prev.slice(0, -1));
    }, []);

    useEffect(() => {
        dialogController.bind(push, pop);
        return () => dialogController.cleanup();
    }, [push, pop]);

    return (
        <>
            {dialogs.map((dialog, i) => (
                <Dialog
                    key={dialog.id}
                    dialog={dialog}
                    zIndex={1000 + i}
                    onClose={() =>
                        setDialogs((prev) =>
                            prev.filter((d) => d.id !== dialog.id)
                        )
                    }
                />
            ))}
        </>
    );
}

const mountedRoots = new Map();

export function mountComponent(targetId, component, props = {}) {
    const el = document.getElementById(targetId);
    if (!el) {
        console.warn(`Target #${targetId} not found`);
        return false;
    }

    if (mountedRoots.has(targetId)) {
        mountedRoots.get(targetId).unmount();
    }

    const root = createRoot(el);
    mountedRoots.set(targetId, root);

    root.render(
        <React.StrictMode>
            <ComponentRenderer name={component} props={props} />
        </React.StrictMode>
    );

    return true;
}

export function unmountComponent(targetId) {
    mountedRoots.get(targetId)?.unmount();
    mountedRoots.delete(targetId);
}

export function openDialog(component, props = {}, title = "", size = "small") {
    if (typeof window.showLoader === 'function') window.showLoader();

    if (!componentRegistry[component]) {
        console.error(`Component "${component}" not registered`);
        return;
    }

    dialogController.push({
        id: crypto.randomUUID(),
        component,
        props,
        title,
        size,
    });
}

export function closeDialog() {
    dialogController.pop();
}

export function cleanupAll() {
    mountedRoots.forEach((root) => root.unmount());
    mountedRoots.clear();
    dialogController.cleanup();
}

// Mount the Application component for dialog management
const dialogRoot = document.createElement("div");
dialogRoot.id = "dialog-root";
document.body.appendChild(dialogRoot);

const dialogRootInstance = createRoot(dialogRoot);
dialogRootInstance.render(
    <React.StrictMode>
        <Application />
    </React.StrictMode>
);

// Mount the main component if root element exists
const rootElement = document.getElementById("root");

if (rootElement) {
    const component = rootElement.getAttribute("component");
    if (component) {
        const props = {
            entity: rootElement.getAttribute("entity"),
        };

        mountComponent("root", component, props);
    }
}

export default Application;
